import { Label, type LabelRepository } from '@telespot/domain';
import { ParseBaseRepository } from '../../parse-base.repository';
import { LabelTopology, ParseLabelMapper } from './parse-label.mapper';

export class ParseLabelRepository extends ParseBaseRepository implements LabelRepository {
  private readonly labelMapper = new ParseLabelMapper(this.parse);

  public async *getLabelsFromUuids(uuids: string[], lang?: string): AsyncGenerator<Label[]> {
    const query = new this.parse.Query(LabelTopology.TABLE).containedIn(LabelTopology.UUID, uuids);

    if (lang) query.equalTo(LabelTopology.LANG, lang);

    const generator = this.getTokenPaginationGenerator({ query, limit: 100 });

    for await (const items of generator) {
      yield items.map((l) => this.labelMapper.toDomain(l));
    }
  }

  public async list(
    lang: string,
    searchTerm?: string,
    limit?: number,
    pagination?: {
      lastItemName: string;
      lastItemUUID: string;
    }
  ): Promise<Record<string, Label[]>> {
    const query = new this.parse.Query(LabelTopology.TABLE);

    // Build aggregation pipeline - an array of stages that transform documents step by step
    const pipeline: any[] = [
      // ========================================
      // STAGE 1: $group - Group documents by UUID
      // ========================================
      // MongoDB's $group stage groups all documents that share the same UUID together.
      // Since labels can exist in multiple languages (en, es, fr, etc.), we want to
      // group all language variants of the same label into a single result.
      {
        $group: {
          // _id: The field to group by (like SQL's GROUP BY).
          // Parse Server's AggregateRouter requires the canonical Mongo key
          // `_id` here (not `objectId`) and explicitly rejects the latter.
          _id: `$${LabelTopology.UUID}`,

          // labels: Collect all complete documents ($$ROOT) into an array
          // $push is an accumulator operator that adds each document to an array
          // $$ROOT is a special variable that refers to the entire original document
          // Result: Each group will have a 'labels' array containing all language variants
          labels: { $push: '$$ROOT' },

          // allValues: Create an array of just the label values (not full documents)
          // This will be used later to create a searchable string across all languages
          // $push adds each document's VALUE field to the array
          // The $ before LabelTopology.VALUE references the field from the current document
          allValues: {
            $push: `$${LabelTopology.VALUE}`,
          },

          // userLangValue: Extract the value in the user's preferred language for pagination
          // We need this to create a consistent sorting cursor based on what the user sees
          userLangValue: {
            // $max: Takes the maximum value from all matching items
            // We use $max with a conditional to get a single value (not an array)
            $max: {
              // $cond: Conditional operator (like a ternary operator: condition ? true : false)
              // Syntax: [$condition, $valueIfTrue, $valueIfFalse]
              $cond: [
                // Condition: Check if the document's language matches the user's language
                // $eq: Equality operator (returns true if both values are equal)
                { $eq: [`$${LabelTopology.LANG}`, lang] },
                // If true: Return the value (this is what the user sees)
                `$${LabelTopology.VALUE}`,
                // If false: Return empty string (so it doesn't affect $max)
                '',
              ],
            },
          },
        },
      },

      // ========================================
      // STAGE 2: $project - Reshape documents and create computed fields
      // ========================================
      // MongoDB's $project stage reshapes documents, adds new computed fields,
      // and controls which fields to include or exclude in the output
      {
        $project: {
          // uuid: Rename the _id field (which contains our UUID from grouping) to 'uuid'
          // '$_id' references the _id field from the previous stage's output
          uuid: '$_id',

          // labels: Include the labels array from the previous stage
          // Setting a field to 1 means "include this field as-is"
          // Setting to 0 would mean "exclude this field"
          labels: 1,

          // searchField: Create a single searchable string from all language
          // values PLUS the label UUID. This lets a single search term match
          // either a translated value (any language) or the UUID itself, using
          // the same case-insensitive regex mechanism downstream.
          searchField: {
            // $concat: combine the reduced language values with the UUID
            $concat: [
              // $reduce: Array reduction operator (like JavaScript's Array.reduce())
              // It iterates over allValues and accumulates a space-separated string
              // Result: "Label 1 Etiqueta 1 Étiquette 1"
              {
                $reduce: {
                  // input: The array to iterate over (our allValues array from Stage 1)
                  input: '$allValues',

                  // initialValue: The starting value for the accumulator (empty string)
                  initialValue: '',

                  // in: The expression to apply for each element
                  // $$value is the accumulator (current result so far)
                  // $$this is the current array element being processed
                  in: {
                    // $concat: combine accumulator, a separator, and the current value
                    // The $cond avoids a leading space when the accumulator is empty
                    $concat: ['$$value', { $cond: [{ $eq: ['$$value', ''] }, '', ' '] }, '$$this'],
                  },
                },
              },

              // Append a separator and the UUID (the $group key, exposed as $_id)
              // so the search term can also match the label's UUID.
              ' ',
              '$_id',
            ],
          },

          // cursorField: Create a sortable pagination cursor by combining value + UUID
          // Format: "Label 1_uuid-123" - this ensures consistent, stable sorting
          // Even if two labels have the same name, the UUID makes them unique
          cursorField: {
            // $concat: Combine the user's language value, underscore separator, and UUID
            $concat: ['$userLangValue', '_', '$_id'],
          },
        },
      },
    ];

    // ========================================
    // STAGE 3: $match (optional) - Filter by search term
    // ========================================
    // MongoDB's $match stage filters documents (like SQL's WHERE clause)
    // Only applied if the user provided a search term
    if (searchTerm) {
      pipeline.push({
        $match: {
          // Filter on the searchField we created (which contains all language values)
          searchField: {
            // $regex: Regular expression pattern matching operator
            // This allows partial string matching (not just exact matches)
            $regex: this.escapeRegExp(searchTerm),

            // $options: Modifiers for the regex pattern
            // 'i' = case-insensitive (matches "Label", "label", "LABEL", etc.)
            $options: 'i',
          },
        },
      });
    }

    // ========================================
    // STAGE 4: $match (optional) - Filter for pagination cursor
    // ========================================
    // Only applied if continuing from a previous page
    // This filters out items we've already seen in previous pages
    if (pagination) {
      pipeline.push({
        $match: {
          // Filter where cursorField is greater than the last item's cursor
          cursorField: {
            // $gt: "Greater than" operator
            // Since cursor is "value_uuid", string comparison gives us consistent ordering
            // This excludes all items up to and including the last item from the previous page
            $gt: `${pagination.lastItemName}_${pagination.lastItemUUID}`,
          },
        },
      });
    }

    // ========================================
    // STAGE 5: $sort - Sort results for consistent pagination
    // ========================================
    // MongoDB's $sort stage orders documents (like SQL's ORDER BY)
    // Always applied to ensure consistent, predictable ordering across pages
    pipeline.push({
      $sort: {
        // Sort by cursorField in ascending order
        // 1 = ascending (A-Z, 0-9), -1 would be descending (Z-A, 9-0)
        // This ensures "Label 1_uuid-123" comes before "Label 2_uuid-456"
        cursorField: 1,
      },
    });

    // ========================================
    // STAGE 6: $limit (optional) - Limit number of results
    // ========================================
    // MongoDB's $limit stage restricts the number of documents passed through
    // Only applied if the user requested a specific page size
    if (limit) {
      pipeline.push({
        // Limit to the specified number of documents
        // This is crucial for pagination - we fetch one page at a time
        $limit: limit,
      });
    }

    // ========================================
    // EXECUTE AGGREGATION
    // ========================================
    // Run the complete pipeline and get results
    // The pipeline executes on the Parse/MongoDB server, not in JavaScript
    const results = await query.aggregate(pipeline);

    // ========================================
    // TRANSFORM RESULTS
    // ========================================
    // Convert the aggregation results into our domain model format
    // MongoDB returns an array of objects, we need Record<string, Label[]>
    const record: Record<string, Label[]> = {};

    // Iterate over each grouped result (each UUID group)
    for (const result of results) {
      // Extract the UUID (might be in 'uuid' or 'objectId' depending on stage)
      const uuid = result.uuid || result.objectId;

      // Extract the array of labels for this UUID (all language variants)
      const labels = result.labels || [];

      // Convert each Parse label object to our domain Label entity
      // Store in the record with UUID as the key
      record[uuid] = labels.map((parseLabel) => {
        return new Label({
          id: parseLabel._id,
          uuid: parseLabel[LabelTopology.UUID],
          value: parseLabel[LabelTopology.VALUE],
          lang: parseLabel[LabelTopology.LANG],
        });
      });
    }

    // Return the complete record: { "uuid-123": [Label(en), Label(es), ...], ... }
    return record;
  }

  public async count(searchTerm?: string): Promise<number> {
    const query = new this.parse.Query(LabelTopology.TABLE);

    // Build aggregation pipeline - an array of stages that transform documents step by step
    // This pipeline is simpler than list() since we only need the count, not the actual documents
    const pipeline: any[] = [
      // ========================================
      // STAGE 1: $group - Group documents by UUID
      // ========================================
      // MongoDB's $group stage groups all documents that share the same UUID together.
      // Since labels exist in multiple languages (en, es, fr, etc.), we want to count
      // each unique label only once, not once per language variant.
      // Example: If "Label 1" exists in 3 languages, we count it as 1 label, not 3.
      {
        $group: {
          // _id: The field to group by (like SQL's GROUP BY).
          // Parse Server's AggregateRouter requires the canonical Mongo key
          // `_id` here (not `objectId`) and explicitly rejects the latter.
          _id: `$${LabelTopology.UUID}`,

          // allValues: Create an array of just the label values (not full documents)
          // This will be used later to create a searchable string across all languages
          // $push is an accumulator operator that adds each document's VALUE to the array
          // The $ before LabelTopology.VALUE references the field from the current document
          // Example result: ["Label 1", "Etiqueta 1", "Étiquette 1"]
          allValues: {
            $push: `$${LabelTopology.VALUE}`,
          },
        },
      },

      // ========================================
      // STAGE 2: $project - Reshape documents and create computed fields
      // ========================================
      // MongoDB's $project stage reshapes documents and adds computed fields
      // We create a searchField that contains all language values as a single string
      {
        $project: {
          // uuid: Rename the _id field (which contains our UUID from grouping) to 'uuid'
          // '$_id' references the _id field from the previous stage's output
          uuid: '$_id',

          // searchField: Create a single searchable string from all language
          // values PLUS the label UUID, mirroring list() so the count reflects
          // the same matches (value in any language, or the UUID itself).
          // Example: "Label 1 Etiqueta 1 Étiquette 1 <uuid>"
          searchField: {
            // $concat: combine the reduced language values with the UUID
            $concat: [
              // $reduce: iterate over allValues, accumulating a space-separated string
              {
                $reduce: {
                  // input: The array of all language values for this UUID
                  input: '$allValues',

                  // initialValue: start empty and build up
                  initialValue: '',

                  // in: combine accumulator, a separator, and the current value
                  // The $cond avoids a leading space when the accumulator is empty
                  in: {
                    $concat: ['$$value', { $cond: [{ $eq: ['$$value', ''] }, '', ' '] }, '$$this'],
                  },
                },
              },

              // Append a separator and the UUID (the $group key, exposed as $_id)
              // so the search term can also match the label's UUID.
              ' ',
              '$_id',
            ],
          },
        },
      },
    ];

    // ========================================
    // STAGE 3: $match (optional) - Filter by search term
    // ========================================
    // MongoDB's $match stage filters documents (like SQL's WHERE clause)
    // Only applied if the user provided a search term
    // This reduces the number of documents before counting
    if (searchTerm) {
      pipeline.push({
        $match: {
          // Filter on the searchField we created (which contains all language values)
          // This allows matching against any language variant
          searchField: {
            // $regex: Regular expression pattern matching operator
            // This allows partial string matching (substring search)
            // Example: searching "Label" will match "Label 1", "My Label", etc.
            $regex: this.escapeRegExp(searchTerm),

            // $options: Modifiers for the regex pattern
            // 'i' = case-insensitive matching
            // This means "Label", "label", "LABEL", "LaBel" all match the same
            $options: 'i',
          },
        },
      });
    }

    // ========================================
    // STAGE 4: $count - Count the remaining documents
    // ========================================
    // MongoDB's $count stage counts all documents that made it through the pipeline
    // It returns a single document with the count value
    pipeline.push({
      // $count: The field name to store the count in
      // The result will be: [{ total: 3 }] or [] if no matches
      // 'total' is just a label we chose; we could name it anything
      $count: 'total',
    });

    // ========================================
    // EXECUTE AGGREGATION
    // ========================================
    // Run the complete pipeline and get results
    // The pipeline executes on the Parse/MongoDB server, not in JavaScript
    const results = await query.aggregate(pipeline);

    // ========================================
    // RETURN COUNT
    // ========================================
    // The $count stage returns an array with one object: [{ total: number }]
    // If there are no matches, it returns an empty array: []
    // We need to handle both cases to avoid errors
    return results.length > 0 ? results[0].total : 0;
  }

  public async save(uuid: string, values: Record<string, string>): Promise<Label[]> {
    // Query for existing labels with this UUID
    const query = new this.parse.Query(LabelTopology.TABLE);
    query.equalTo(LabelTopology.UUID, uuid);
    const existingLabels = await query.find(this.options);

    // Prepare labels to save
    const labelsToSave: Parse.Object[] = [];
    const ParseLabel = this.parse.Object.extend(LabelTopology.TABLE);

    // Process each language in the values object
    Object.keys(values).forEach((lang) => {
      const value = values[lang];
      const hasValue = value && value.trim().length > 0;

      // Find existing label for this language
      const existingLabel = existingLabels.find((label) => label.get(LabelTopology.LANG) === lang);

      if (!existingLabel && !hasValue) {
        // No existing label and no value to save - skip
        return;
      }

      // Create or update label
      const labelToSave = existingLabel ? ParseLabel.createWithoutData(existingLabel.id) : new ParseLabel();

      labelToSave.set(LabelTopology.UUID, uuid);
      labelToSave.set(LabelTopology.LANG, lang);
      labelToSave.set(LabelTopology.VALUE, value);

      labelsToSave.push(labelToSave);
    });

    // Batch save all labels
    const savedParseLabels = await this.parse.Object.saveAll(labelsToSave, this.options);

    // Convert to domain entities
    return savedParseLabels.map(
      (parseLabel) =>
        new Label({
          id: parseLabel.id,
          uuid: parseLabel.get(LabelTopology.UUID),
          value: parseLabel.get(LabelTopology.VALUE),
          lang: parseLabel.get(LabelTopology.LANG),
        })
    );
  }
}
